// Cadastral Service - Auto-complete cadastral numbers for parcels
import firebaseAuthService from './firebaseAuthService';

class CadastralService {
  constructor() {
    this.cadastralData = {};
    this.loadCadastralData();
  }

  // Load cadastral data from localStorage
  loadCadastralData() {
    const currentSession = firebaseAuthService.getCurrentSession();
    if (!currentSession?.farm?.id) {
      console.log('🔍 CadastralService: No current session or farm ID');
      return;
    }

    const farmId = currentSession.farm.id;
    const cadastralKey = `cadastralDatabase_${farmId}`;
    const savedData = localStorage.getItem(cadastralKey);
    
    console.log(`🔍 CadastralService: Loading data for farmId=${farmId}, key=${cadastralKey}`);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        console.log('🔍 CadastralService: Raw data loaded:', parsed);
        
        // Check if this is Firebase wrapped data
        if (parsed.dataType === 'cadastralDatabase' || parsed.synced !== undefined) {
          console.log('📦 CadastralService: Found Firebase wrapped data, extracting...');
          this.cadastralData = parsed.data || parsed.actualData || {};
        } else {
          console.log('📦 CadastralService: Using direct data');
          this.cadastralData = parsed;
        }
        
        console.log('✅ CadastralService: Final cadastral data:', this.cadastralData);
        console.log('📊 CadastralService: Available files:', Object.keys(this.cadastralData));
        
        // Clean up metadata entries that shouldn't be in cadastral data
        const cleanedData = {};
        Object.entries(this.cadastralData).forEach(([fileName, fileData]) => {
          // Skip metadata entries
          if (fileName === 'lastModified' || fileName === 'dataType' || fileName === 'synced' || fileName === 'farmId') {
            console.log(`🧹 Skipping metadata entry: ${fileName}`);
            return;
          }
          
          if (fileData && typeof fileData === 'object' && fileData.tNumbers) {
            cleanedData[fileName] = fileData;
            console.log(`📋 File "${fileName}":`, {
              tNumbers: fileData.tNumbers,
              recordCount: fileData.recordCount,
              ownerCount: Object.keys(fileData.owners || {}).length,
              sampleOwners: Object.keys(fileData.owners || {}).slice(0, 3)
            });
          } else {
            console.log(`⚠️ Skipping invalid file entry: ${fileName}`, fileData);
          }
        });
        
        this.cadastralData = cleanedData;
        
        // Log T numbers for debugging
        const allTNumbers = this.getAvailableTNumbers();
        console.log('📍 CadastralService: Available T numbers:', allTNumbers);
        
      } catch (error) {
        console.error('❌ CadastralService: Error loading cadastral data:', error);
        this.cadastralData = {};
      }
    } else {
      console.log('📂 CadastralService: No cadastral data found in localStorage');
      this.cadastralData = {};
    }
  }

  // Find cadastral number for a given T number, owner name, and surface
  findCadastralNumber(tNumber, ownerName, userSurface, comuna = null) {
    console.log(`🔍 CadastralService.findCadastralNumber called with: T=${tNumber}, Owner="${ownerName}", Surface=${userSurface}, Comuna="${comuna}"`);

    if (!tNumber || !ownerName) {
      console.log('❌ Missing required parameters (tNumber or ownerName)');
      return null;
    }

    console.log('📊 Current cadastral data structure:', Object.keys(this.cadastralData));

    // Find files that cover this T number (and optionally filter by comuna)
    const relevantFiles = Object.entries(this.cadastralData).filter(([fileName, fileData]) => {
      const hasT = fileData.tNumbers && fileData.tNumbers.includes(tNumber);
      const matchesComuna = !comuna || !fileData.comuna || fileData.comuna.toLowerCase() === comuna.toLowerCase();
      console.log(`📋 File "${fileName}" has T numbers: ${fileData.tNumbers?.join(', ') || 'none'}, includes ${tNumber}: ${hasT}, comunaMatch: ${matchesComuna}`);
      return hasT && matchesComuna;
    });

    console.log(`📋 Found ${relevantFiles.length} relevant files for ${tNumber}`);

    if (relevantFiles.length === 0) {
      console.log(`📋 No cadastral files found for ${tNumber}`);
      return null;
    }

    // Collect ALL matches for this owner
    const allMatches = [];

    // Search for owner name in relevant files
    for (const [fileName, fileData] of relevantFiles) {
      const owners = fileData.owners || {};
      console.log(`👥 File "${fileName}" has ${Object.keys(owners).length} owners:`, Object.keys(owners));
      
      // Try exact match first
      if (owners[ownerName]) {
        console.log(`✅ Exact match found for "${ownerName}" in file "${fileName}"`);
        const ownerData = owners[ownerName];
        // Check if this is an array (multiple parcels) or single parcel
        if (Array.isArray(ownerData)) {
          // Multiple parcels for this owner
          ownerData.forEach(parcelData => {
            allMatches.push(this.createResult(parcelData, userSurface, fileName));
          });
        } else {
          // Single parcel
          allMatches.push(this.createResult(ownerData, userSurface, fileName));
        }
        continue; // Found exact match, skip other matching attempts for this file
      }

      // Try case-insensitive match
      const ownerNameLower = ownerName.toLowerCase();
      let foundCaseInsensitive = false;
      for (const [storedName, ownerData] of Object.entries(owners)) {
        if (storedName.toLowerCase() === ownerNameLower) {
          console.log(`✅ Case-insensitive match found: "${storedName}" matches "${ownerName}"`);
          if (Array.isArray(ownerData)) {
            ownerData.forEach(parcelData => {
              allMatches.push(this.createResult(parcelData, userSurface, fileName));
            });
          } else {
            allMatches.push(this.createResult(ownerData, userSurface, fileName));
          }
          foundCaseInsensitive = true;
          break;
        }
      }
      if (foundCaseInsensitive) continue;

      // Try flexible name matching (remove dots, middle initials, extra spaces)
      const normalizedUserName = this.normalizeName(ownerName);
      let foundFlexible = false;
      for (const [storedName, ownerData] of Object.entries(owners)) {
        const normalizedStoredName = this.normalizeName(storedName);
        if (normalizedStoredName === normalizedUserName) {
          console.log(`✅ Flexible match found: "${storedName}" matches "${ownerName}" (normalized)`);
          if (Array.isArray(ownerData)) {
            ownerData.forEach(parcelData => {
              allMatches.push(this.createResult(parcelData, userSurface, fileName, true));
            });
          } else {
            allMatches.push(this.createResult(ownerData, userSurface, fileName, true));
          }
          foundFlexible = true;
          break;
        }
      }
      if (foundFlexible) continue;

      // Try partial match (contains)
      for (const [storedName, ownerData] of Object.entries(owners)) {
        if (storedName.toLowerCase().includes(ownerNameLower) || 
            ownerNameLower.includes(storedName.toLowerCase())) {
          console.log(`✅ Partial match found: "${storedName}" partially matches "${ownerName}"`);
          if (Array.isArray(ownerData)) {
            ownerData.forEach(parcelData => {
              allMatches.push(this.createResult(parcelData, userSurface, fileName, true));
            });
          } else {
            allMatches.push(this.createResult(ownerData, userSurface, fileName, true));
          }
          break;
        }
      }
    }

    if (allMatches.length === 0) {
      console.log(`📋 No cadastral data found for owner "${ownerName}" in ${tNumber}`);
      return null;
    }

    console.log(`📋 Found ${allMatches.length} matches for "${ownerName}"`);

    // If only one match, return it directly
    if (allMatches.length === 1) {
      return allMatches[0];
    }

    // Multiple matches - return object with all options
    return {
      multipleMatches: true,
      matches: allMatches,
      ownerName,
      tNumber,
      userSurface
    };
  }

  // Create result object with surface matching logic
  createResult(ownerData, userSurface, fileName, isPartialMatch = false) {
    let storedSurface = ownerData.surface; // Already in hectares from parsing
    
    // Fix for legacy data that might not have been converted properly
    // If stored surface is suspiciously large (>50 ha), it might be in mp instead of ha
    if (storedSurface > 50) {
      console.log(`⚠️ Large surface detected (${storedSurface}), assuming it's in mp and converting to ha`);
      storedSurface = storedSurface / 10000;
    }
    
    // Allow 0.01 hectare tolerance (100 mp tolerance)
    const surfaceMatch = Math.abs(storedSurface - userSurface) <= 0.01;
    
    return {
      cadastralNumber: ownerData.cadastralNumber,
      storedSurface,
      userSurface,
      surfaceMatch,
      address: ownerData.address,
      sourceFile: fileName,
      isPartialMatch,
      autoFill: surfaceMatch && !isPartialMatch,
      message: this.generateMessage(ownerData, userSurface, surfaceMatch, isPartialMatch)
    };
  }

  // Generate user-friendly message
  generateMessage(ownerData, userSurface, surfaceMatch, isPartialMatch) {
    if (isPartialMatch) {
      return `Potrivire parțială găsită. Verificați numele proprietarului.`;
    }
    
    if (surfaceMatch) {
      return `✅ Potrivire exactă găsită! Număr cadastral auto-completat.`;
    } else {
      // Use the corrected surface value for the message
      let displaySurface = ownerData.surface;
      if (displaySurface > 50) {
        displaySurface = displaySurface / 10000;
      }
      return `⚠️ Am găsit numele cu numărul cadastral ${ownerData.cadastralNumber} dar cu suprafața ${displaySurface.toFixed(2)} ha (tu ai introdus ${userSurface} ha). Accepți numărul cadastral?`;
    }
  }

  // Get all T numbers that have cadastral data
  getAvailableTNumbers() {
    const tNumbers = new Set();
    
    Object.values(this.cadastralData).forEach(fileData => {
      if (fileData.tNumbers) {
        fileData.tNumbers.forEach(t => tNumbers.add(t));
      }
    });
    
    return Array.from(tNumbers).sort();
  }

  // Get owner suggestions for autocomplete
  getOwnerSuggestions(tNumber, searchTerm) {
    if (!searchTerm || searchTerm.length < 2) return [];

    const relevantFiles = Object.entries(this.cadastralData).filter(([fileName, fileData]) => 
      fileData.tNumbers && fileData.tNumbers.includes(tNumber)
    );

    const suggestions = [];
    const searchLower = searchTerm.toLowerCase();

    for (const [fileName, fileData] of relevantFiles) {
      const owners = fileData.owners || {};
      
      for (const [ownerName, ownerData] of Object.entries(owners)) {
        if (ownerName.toLowerCase().includes(searchLower)) {
          suggestions.push({
            name: ownerName,
            cadastralNumber: ownerData.cadastralNumber,
            surface: ownerData.surface,
            address: ownerData.address
          });
        }
      }
    }

    // Remove duplicates and limit results
    const uniqueSuggestions = suggestions.filter((item, index, self) => 
      index === self.findIndex(t => t.name === item.name)
    );

    return uniqueSuggestions.slice(0, 10); // Limit to 10 suggestions
  }

  // Normalize name for flexible matching
  normalizeName(name) {
    if (!name) return '';
    
    return name
      .toLowerCase()
      .replace(/\./g, '') // Remove dots
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim()
      .split(' ')
      .filter(part => part.length > 1) // Remove single letter middle initials
      .join(' ');
  }

  // Refresh data (call when cadastral data is updated)
  refresh() {
    this.loadCadastralData();
  }
}

// Create singleton instance
const cadastralService = new CadastralService();

// Listen for cadastral data updates
window.addEventListener('cadastralDataUpdated', () => {
  cadastralService.refresh();
});

export default cadastralService; 